/**
 * free-gift.js (v2)
 *
 * Keeps a $0 "gift" line item in sync with the cart total:
 *   total >= threshold -> exactly one gift line exists
 *   total <  threshold -> no gift line exists
 *
 * Load this ONCE, globally (layout/theme.liquid), so it also runs on pages
 * where the drawer / progress-bar markup isn't rendered (e.g. /cart).
 * A second <script> tag is harmless: the init guard makes it a no-op.
 *
 * Debugging: run  window.FREE_GIFT_DEBUG = true  in the browser console,
 * then change the cart and watch the "[free-gift]" logs.
 */
(function () {
  if (window.__freeGiftInit) return;
  window.__freeGiftInit = true;

  // Fallback values, used until the optional "Cart rewards" theme settings exist.
  // Keep in sync with free-gift-bar.liquid. Threshold is in cents.
  var DEFAULTS = { variantId: '50522676625487', threshold: 7500 };

  var nativeFetch = window.fetch.bind(window);
  var timer = null;
  var running = false;
  var rerun = false;
  var addBlockedUntil = 0;

  function log() {
    if (window.FREE_GIFT_DEBUG) {
      console.log.apply(console, ['[free-gift]'].concat([].slice.call(arguments)));
    }
  }
  function warn() {
    console.warn.apply(console, ['[free-gift]'].concat([].slice.call(arguments)));
  }

  function route(key, fallback) {
    return (window.routes && window.routes[key]) || fallback;
  }

  function getConfig() {
    // 1) Theme settings (window.FREE_GIFT_CONFIG), only if that optional setup is installed
    var c = window.FREE_GIFT_CONFIG;
    if (c) {
      if (!c.enabled) return null;
      var vid = String(c.variantId || '');
      var th = parseInt(c.threshold, 10);
      if (!vid || !th) return null;
      return { variantId: vid, threshold: th };
    }
    // 2) Data attributes on the progress bar, else the DEFAULTS above
    var bar = document.querySelector('.free-gift-bar');
    var variantId = (bar && bar.dataset.fgbVariantId) || DEFAULTS.variantId;
    var threshold = parseInt((bar && bar.dataset.fgbThreshold) || DEFAULTS.threshold, 10);
    if (!variantId || !threshold) return null;
    return { variantId: String(variantId), threshold: threshold };
  }

  function postJson(url, payload) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res
        .json()
        .catch(function () { return {}; })
        .then(function (body) {
          return { ok: res.ok, status: res.status, body: body };
        });
    });
  }

  function findGiftLine(cart, variantId) {
    return cart.items.find(function (item) {
      return (
        String(item.variant_id) === String(variantId) &&
        item.properties &&
        item.properties._gift === 'true'
      );
    });
  }

  // ---- UI refresh after we add/remove the gift ----------------------------
  function refreshUI() {
    // Cart page: a reload keeps rows, totals and line numbers in sync.
    if (document.querySelector('cart-items')) {
      window.location.reload();
      return Promise.resolve();
    }

    var base = route('cart_url', '/cart');
    var selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart-rewards-panel'];

    var drawer = nativeFetch(base + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var html = new DOMParser().parseFromString(text, 'text/html');
        selectors.forEach(function (sel) {
          var target = document.querySelector(sel);
          var source = html.querySelector(sel);
          if (target && source) target.replaceWith(source);
        });
      });

    var bubble = nativeFetch(base + '?section_id=cart-icon-bubble')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var html = new DOMParser().parseFromString(text, 'text/html');
        var source = html.querySelector('.shopify-section');
        var target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      });

    return Promise.all([drawer, bubble]).catch(function (e) {
      warn('UI refresh failed', e);
    });
  }

  // ---- Core sync -----------------------------------------------------------
  function sync() {
    if (running) {
      rerun = true; // an event arrived mid-sync: run once more when done
      return Promise.resolve();
    }
    var config = getConfig();
    if (!config) return Promise.resolve();

    running = true;
    var addUrl = route('cart_add_url', '/cart/add') + '.js';
    var changeUrl = route('cart_change_url', '/cart/change') + '.js';

    return nativeFetch(route('cart_url', '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // NOTE: items_subtotal_price ignores order-level discount codes (e.g. WELCOME). Use cart.total_price
        // instead if the threshold should count the amount AFTER discounts.
        var qualifies = cart.items_subtotal_price >= config.threshold;
        var giftLine = findGiftLine(cart, config.variantId);
        log("subtotal", cart.items_subtotal_price, "total after discounts", cart.total_price, 'threshold', config.threshold, 'qualifies', qualifies, 'giftInCart', !!giftLine);

        if (qualifies && !giftLine) {
          if (Date.now() < addBlockedUntil) return false;
          return postJson(addUrl, {
            items: [{ id: config.variantId, quantity: 1, properties: { _gift: 'true' } }],
          }).then(function (res) {
            if (!res.ok) {
              addBlockedUntil = Date.now() + 10000; // don't hammer a failing request
              warn('Could not add gift. Status', res.status, res.body);
              return false;
            }
            return true;
          });
        }

        if (!qualifies && giftLine) {
          return postJson(changeUrl, { id: giftLine.key, quantity: 0 }).then(function (res) {
            if (!res.ok) { warn('Could not remove gift', res.status, res.body); return false; }
            return true;
          });
        }

        if (qualifies && giftLine && giftLine.quantity > 1) {
          return postJson(changeUrl, { id: giftLine.key, quantity: 1 }).then(function (res) {
            return res.ok;
          });
        }

        return false;
      })
      .then(function (changed) {
        return changed ? refreshUI() : null;
      })
      .catch(function (e) {
        warn('sync failed', e);
      })
      .then(function () {
        running = false;
        if (rerun) {
          rerun = false;
          scheduleSync();
        }
      });
  }

  function scheduleSync() {
    clearTimeout(timer);
    timer = setTimeout(sync, 300);
  }

  // ---- Triggers ------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) scheduleSync(); // back/forward cache
  });

  document.addEventListener('cart:update', scheduleSync);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleSync);
  }

  // Catch cart changes from ANY source (product cards, quick add, apps...),
  // even if they never publish a pub/sub event. Our own requests use
  // nativeFetch, so they never re-trigger this.
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = nativeFetch.apply(window, arguments);
    if (CART_MUTATION.test(url)) p.then(scheduleSync, function () {});
    return p;
  };
})();
